/** Orders, delivery zones and the customer's confirmation page (docs/specs/sales.md, docs/specs/shipping.md). */
export const orders = {
    title: "Orders",
    description: "Online and phone orders: confirm, pack, ship and follow up.",
    new: "New order",
    back: "All orders",
    empty: "No orders yet.",
    conflict: "Someone else changed this order. The latest version is shown; try again.",
    filters: {
        status: "Status",
        channel: "Channel",
        phone: "Phone",
        any: "All",
        tag: "Tag",
        assignee: "Assigned to",
        anyone: "Anyone",
        unassigned: "Nobody",
        from: "From",
        to: "To",
        number: "Order number",
    },
    returns: {
        title: "Returns",
        description: "Goods back from customers and exchanges.",
        empty: "No returns yet.",
        newReturn: "Take a return",
        newExchange: "Exchange",
        titleReturn: "Return from {{number}}",
        titleExchange: "Exchange for {{number}}",
        lines: "What comes back",
        sold: "Sold {{qty}}, back already {{returned}}",
        qtyBack: "Quantity back",
        condition: "Condition",
        conditions: { sellable: "Sellable (back to stock)", damaged: "Damaged (written off)" },
        reason: "Reason",
        reasons: {
            damaged: "Damaged",
            wrong_item: "Wrong item",
            not_as_described: "Not as described",
            changed_mind: "Changed mind",
            late_delivery: "Late delivery",
            other: "Other",
        },
        location: "Back into stock at",
        refundMethod: "Refund",
        refundMethods: {
            none: "No refund",
            cash: "Cash",
            bkash: "bKash",
            nagad: "Nagad",
            bank: "Bank",
            store_credit: "Store credit (lowers dues)",
        },
        refundAmount: "Refund amount",
        refundHint: "Empty = the full value of the goods.",
        newItems: "Replacement items",
        exchangeHint: "The returned value pays for the replacement; anything left is refunded, anything missing is due.",
        submitReturn: "Post return",
        submitExchange: "Post exchange",
        nothing: "Enter a quantity for at least one line.",
        posted: "Return {{number}} posted.",
        exchanged: "Exchange posted: new order {{number}}.",
        kinds: { delivery_return: "Courier return", customer_return: "Customer return", exchange: "Exchange" },
        columns: {
            number: "Return",
            order: "Order",
            kind: "Type",
            reason: "Reason",
            total: "Value",
            refund: "Refund",
            date: "Date",
        },
        value: "Value",
        replacement: "Replacement order",
    },
    desk: {
        labels: "Tags and assignee",
        tags: "Tags",
        tagsHint: "Comma-separated, up to 10",
        assignee: "Assigned to",
        unassigned: "Nobody",
        save: "Save",
        saved: "Saved.",
        notes: "Internal notes",
        notesHint: "Only your team sees these.",
        noNotes: "No notes yet.",
        addNote: "Add note",
        note: "Note",
    },
    print: {
        title: "Print",
        invoice_a4: "Invoice (A4)",
        invoice_80mm: "Invoice (80 mm)",
        packing_slip: "Packing slip",
        pick_list: "Pick list",
        working: "Preparing the PDF…",
        ready: "Your PDF is ready.",
        open: "Open PDF",
    },
    bulk: {
        selected: "{{count}} selected",
        confirm: "Confirm",
        status: "Move to…",
        print: "Print…",
        done: "{{ok}} done, {{failed}} not done.",
    },
    views: {
        title: "Saved views",
        none: "All orders",
        save: "Save view",
        name: "View name",
        shared: "Share with the team",
        delete: "Delete view",
        saved: "View saved.",
        deleted: "View deleted.",
    },
    columns: {
        number: "Order",
        date: "Date",
        phone: "Phone",
        status: "Status",
        payment: "Payment",
        total: "Total",
        age: "Waiting",
    },
    status: {
        draft: "Draft",
        pending: "Awaiting confirmation",
        confirmed: "Confirmed",
        packed: "Packed",
        shipped: "Shipped",
        delivered: "Delivered",
        partially_delivered: "Partly delivered",
        returned: "Returned",
        cancelled: "Cancelled",
    },
    payment: {
        unpaid: "Unpaid",
        partially_paid: "Partly paid",
        paid: "Paid",
        cod_pending: "Cash on delivery",
        refunded: "Refunded",
    },
    channel: {
        pos: "POS",
        manual: "Manual",
        facebook: "Facebook",
        whatsapp: "WhatsApp",
        phone: "Phone",
        storefront: "Website",
    },
    actions: {
        confirmed: "Confirm",
        packed: "Mark packed",
        shipped: "Mark shipped",
        delivered: "Mark delivered",
        returned: "Mark returned",
        cancelled: "Cancel order",
        pending: "Send for confirmation",
        edit: "Edit",
        sendSms: "Send confirmation SMS",
        merge: "Merge into this order",
        call: "Call",
        bulkConfirm: "Confirm selected",
    },
    done: {
        created: "Order created.",
        saved: "Order saved.",
        status: "Order updated.",
        merged: "Orders merged.",
        smsSent: "Confirmation SMS sent.",
        bulkQueued: "Confirming {{count}} orders…",
    },
    cancelConfirm: {
        title: "Cancel this order?",
        description: "Reserved stock goes back to available. This cannot be undone.",
        reason: "Reason (optional)",
    },
    form: {
        titleNew: "New order",
        titleEdit: "Edit {{number}}",
        descriptionNew: "Find the customer by phone, add products and the address. Totals are checked when you save.",
        customer: "Customer",
        phone: "Mobile number",
        lookup: "Find",
        found: "Customer: {{name}}",
        notFound: "No customer with this number yet; the order keeps the number.",
        savedAddress: "Saved address",
        newAddress: "Type a new address",
        recipient: "Recipient name",
        division: "Division",
        district: "District",
        upazila: "Upazila / Thana",
        addressLine: "House, road, area",
        location: "Ships from",
        channel: "Channel",
        products: "Products",
        quantity: "Quantity",
        unitPrice: "Price",
        discountPercent: "Discount %",
        billDiscount: "Order discount",
        weight: "Weight (grams)",
        deliveryCharge: "Delivery charge",
        deliveryAuto: "From zone: {{zone}}",
        deliveryTyped: "Typed (overrides the zone)",
        useZone: "Use the zone's charge",
        advance: "Advance received",
        advanceAccount: "Received into",
        advanceAccountHint: "Recorded as a payment for this order.",
        advanceMethod: "Paid by",
        advanceReference: "Transaction ID",
        note: "Note",
        subtotal: "Subtotal",
        estimatedTotal: "Estimated total",
        totalHint: "VAT and the final total are calculated by the server when you save.",
        saveDraft: "Save draft",
        savePending: "Save and send for confirmation",
        save: "Save changes",
        noLines: "Add at least one product.",
    },
    detail: {
        lines: "Products",
        totals: "Totals",
        address: "Delivery address",
        timeline: "Timeline",
        noAddress: "No delivery address.",
        created: "Created {{date}}",
        lineDiscount: "Discounts",
        tax: "VAT",
        delivery: "Delivery",
        total: "Total",
        advance: "Advance",
        duplicates: "Possible duplicates",
        duplicatesBody: "Other open orders for this phone placed close to this one.",
        returned: "{{qty}} returned",
        reason: "Reason: {{reason}}",
    },
    calls: {
        title: "Call list",
        description: "Orders awaiting confirmation. Call the customer, then confirm or cancel.",
        empty: "No orders are waiting for confirmation.",
        bulkDone: "{{ok}} confirmed, {{failed}} not confirmed.",
        failedRow: "{{number}}: {{reason}}",
    },
    zones: {
        title: "Delivery zones",
        description: "Delivery charge by area. The order screen picks the zone from the address.",
        name: "Zone name",
        baseCharge: "Base charge",
        freeWeight: "Included weight (grams)",
        weightStep: "Then per (grams)",
        stepCharge: "Charge per step",
        daysMin: "Delivery in (from, days)",
        daysMax: "Delivery in (to, days)",
        daysHint: "Shown to shoppers on the product page, e.g. 1–2 days. Leave empty to not show it.",
        isDefault: "Default zone (areas not listed elsewhere)",
        areas: "Areas",
        addArea: "Add area",
        wholeDistrict: "Whole district",
        noAreas: "No areas.",
        add: "Add zone",
        archive: "Archive",
        saved: "Zone saved.",
        settings: "Order settings",
        duplicateWindow: "Duplicate warning window (hours)",
        autoSms: "Send the confirmation SMS when an order awaits confirmation",
        settingsSaved: "Settings saved.",
    },
    confirm: {
        title: "Confirm your order",
        from: "Order {{number}} from {{shopName}}",
        delivery: "Delivery",
        total: "Total",
        confirm: "Yes, confirm my order",
        cancel: "Cancel my order",
        confirmed: "Thank you! Your order is confirmed.",
        cancelled: "Your order has been cancelled.",
        closed: "This link can no longer be used.",
        notFound: "This link is not valid.",
    },
};
//# sourceMappingURL=orders.js.map